/* eslint-disable @typescript-eslint/no-explicit-any */

import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../service/routing.service';
import { NewsFeedService } from './news-feed/news-feed.service';
import { AuthService } from 'src/app/service/auth.service';
import { ConfigService } from 'src/app/service/config.service';
import { StoriesService } from 'src/app/shared/services/data-api/stories.service';
import { NewsService } from 'src/app/shared/services/data-api/news.service';
import { TopNewsService } from 'src/app/shared/services/data-api/top-news.service';
import {
  NewsfeedOptions,
  NewsfeedParams,
  NewsfeedResponse,
  NewsfeedData,
} from './news-newsfeed-interface/news-feed.interfaces';
import {
  MainNewsfeedREFRESH_PARAMS,
  NewsType,
} from './news-newsfeed-interface/news-type.constant';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-news-newsfeed',
  templateUrl: './news-newsfeed.component.html',
  styleUrls: ['./news-newsfeed.component.scss'],
})
export class MainNewsfeedComponent {
  newsfeedData: NewsfeedData = {};
  params: NewsfeedParams = {};
  previousValue: NewsfeedParams = {};
  newstype: string = '';
  project: any;
  private static readonly REFRESH_PARAMS = MainNewsfeedREFRESH_PARAMS;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private newsFeedService: NewsFeedService,
    public auth: AuthService,
    public routing: RoutingService,
    public config: ConfigService,
    private storiesService: StoriesService,
    private newsService: NewsService,
    private topNewsService: TopNewsService,
  ) {
    this.initializeRouteSubscription();
  }
  private initializeRouteSubscription(): void {
    this.route.queryParams.subscribe((params) => {
      this.handleQueryParams(params);
    });
  }
  private handleQueryParams(params: NewsfeedParams): void {
    this.params = params;

    if (this.shouldFetchNewsfeed(params)) {
      this.newstype = this.params.newstype || NewsType.CHRONOLOGICAL;
      this.fetchNewsfeedData();
    } else if (this.isDefaultGroupAndId(params)) {
      this.resetNewsfeedData();
    }
    this.previousValue = params;
  }

  private shouldFetchNewsfeed(params: NewsfeedParams): boolean {
    return (
      !this.isEventsMissingContext(params) &&
      (params.id || params.isin || this.routing.isHot()) &&
      this.routing.reFresh(
        params,
        this.previousValue,
        MainNewsfeedComponent.REFRESH_PARAMS,
      )
    );
  }

  private isEventsMissingContext(params: NewsfeedParams): boolean {
    if (params.newstype !== 'Events') return false;
    if (!params.eventid) return true;
    if (this.routing.isScanner() && !params.tag) return true;
    return false;
  }

  private readonly newsFeedHandlers = new Map<
    NewsType | string,
    (handleOnlyFeed: boolean) => void
  >([
    [NewsType.RELEVANT, () => this.fetchTopNewsNewsfeed()],
    [NewsType.STORIES, () => this.fetchStories()],
    [
      NewsType.CHRONOLOGICAL,
      (handleOnlyFeed) => this.fetchChronologicalAllNewdata(handleOnlyFeed),
    ],
    [
      NewsType.BOOKMARKED,
      (handleOnlyFeed) => this.fetchBookMarkedNewsData(handleOnlyFeed),
    ],
    [
      NewsType.FLAGGED,
      (handleOnlyFeed) => this.fetchFlaggedNewsData(handleOnlyFeed),
    ],
    [
      NewsType.DISMISSED,
      (handleOnlyFeed) => this.fetchDismissedNewsData(handleOnlyFeed),
    ],
    [NewsType.EVENTS, () => this.fetchEventSnippetsNewsData()],
    ['default', () => this.fetchEventSnippetsNewsData()],
  ]);

  private fetchNewsfeedData(): void {
    const sentimentFilterExists =
      this.params.filter &&
      Array.isArray(this.params.filter) &&
      this.params.filter.length > 0;
    if (sentimentFilterExists) {
      this.newsService
        .getAllNews({
          customParams: {
            size: 1,
          },
          body: {
            type: this.setNewsContentTypeBasedOnParams(),
            skipSentimentFilter: true,
          },
        })
        .subscribe({
          next: (result) => this.handleCountSuccess(result),
          error: () => this.handleCountError(),
        });
    }
    const handler =
      this.newsFeedHandlers.get(this.newstype as NewsType) ||
      this.newsFeedHandlers.get('default');

    handler?.(sentimentFilterExists);
  }

  private setNewsContentTypeBasedOnParams(): string {
    if (this.routing.isQuery() || this.routing.isSearch()) {
      return 'search_query';
    } else {
      return 'entities';
    }
  }

  private fetchChronologicalAllNewdata(handleOnlyFeed): void {
    this.newsService
      .getAllNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchBookMarkedNewsData(handleOnlyFeed): void {
    this.newsService
      .getBookMarkedNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchFlaggedNewsData(handleOnlyFeed): void {
    this.newsService
      .getFlaggedNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchDismissedNewsData(handleOnlyFeed): void {
    this.newsService
      .getDismissedNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchStories(): void {
    this.storiesService
      .getStories({
        customParams: {
          size: this.params.size || 100,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: this.handleStoriesSuccess.bind(this),
        error: this.handleError.bind(this),
      });
  }

  private fetchTopNewsNewsfeed(): void {
    this.topNewsService
      .getTopNews({
        customParams: {
          size: this.params.size || 100,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: this.handleStoriesSuccess.bind(this),
        error: this.handleError.bind(this),
      });
  }

  private fetchEventSnippetsNewsData(): void {
    this.newsFeedService
      .newsFeed(
        this.createNewsfeedOptions(this.newstype !== 'Events' ? 50 : 1000),
      )
      .subscribe({
        next: (result) => {
          this.handleBothSuccess(result);
        },
        error: () => this.handleError(),
      });
  }

  private createNewsfeedOptions(size: number): NewsfeedOptions {
    return {
      newsType: this.newstype as NewsType,
      returnEvents: this.newstype === 'Events',
      params: this.params,
      custom: {
        next: null,
        size,
        starred_only: this.newstype === 'Bookmarked',
        audited_only: this.newstype === 'Flagged',
        dismissed_only: this.newstype === 'Dismissed',
        top_news_score_threshold: false,
        license: false,
        title: false,
      },
    };
  }

  private handleStoriesSuccess(result: NewsfeedResponse): void {
    if (result.stories) {
      this.newsfeedData = result;
    } else {
      // If result is a stories array directly
      this.newsfeedData = {
        stories: Array.isArray(result) ? result : [],
        reqError: false,
      };
    }

    this.finalizeSuccess();
  }

  private handleCountSuccess(result: NewsfeedResponse): void {
    // For now, update the single data source
    // This method can be removed once all references are updated
    this.newsfeedData = { ...result, reqError: false };
  }

  private handleFeedSuccess(result: NewsfeedResponse): void {
    this.newsfeedData = { ...result, reqError: false };
  }

  private handleBothSuccess(result: NewsfeedResponse): void {
    this.newsfeedData = { ...result, reqError: false };
  }

  private handleCountError(): void {
    // Preserved for compatibility, can be removed later
    this.newsfeedData = { ...this.newsfeedData, reqError: true };
  }

  private handleFeedError(): void {
    this.newsfeedData = { reqError: true };
  }

  private handleError(): void {
    this.newsfeedData = { reqError: true };
  }

  private finalizeSuccess(): void {
    // Set error state to false
    this.newsfeedData = {
      ...this.newsfeedData,
      reqError: false,
      tabsEventsFromScanner: this.params.tabsEventsFromScanner || undefined,
    };

    this.router.navigate([], {
      queryParams: { tabsEventsFromScanner: null },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  private isDefaultGroupAndId(params: NewsfeedParams): boolean {
    return params.groupId === 'default' && params.id === 'default';
  }

  // Getter methods for template compatibility
  get newsfeedDataFeed(): NewsfeedData {
    return this.newsfeedData;
  }

  get newsfeedDataCount(): NewsfeedData {
    return this.newsfeedData;
  }

  private resetNewsfeedData(): void {
    this.newsfeedData = { reqError: false };
  }
}
